import React, {MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {Table, TableBody,} from "semantic-ui-react"
import {EventHandlers, maxRowLimit} from "../creator"
import {I18nIndividual as GedcomIndividual} from "../../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../../model/gedcom-family"
import {CreatorIndiHeader} from "./creator-indi-header"
import {IndiRow} from "./creator-indi-row"
import {CreatorTableFooter} from "../creator-footer"
import {createRow, restoredSession, useAutoScroll} from "../../utils/creator-utils"
import {FormattedMessage, useIntl} from "react-intl"
import {useLocation} from "react-router"
import {withContactMessage} from "../../error/contact-message"
import {useRelatedDeps} from "../creator-related-deps"


export function CreatorIndiTable({ data, lastId, familiesData, familiesEnabled, eventHandlers }: {
    data: GedcomIndividual[]
    lastId: MutableRefObject<number>
    familiesData?: GedcomFamily[]
    familiesEnabled: boolean
    eventHandlers: EventHandlers
}) {
    const location = useLocation()
    const scrollRef = useRef<HTMLDivElement>(null)
    const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null)
    const placeholderInitialized = useRef(false)
    const [selectedRowId, setSelectedRowId] = useState<string>()
    const from = location.state?.from ?? null
    const intl = useIntl()

    const rowLimitError = withContactMessage(
        {id: "creator.rows.limit"},
        intl,
        { setContactModalOpen: eventHandlers.setContactModalOpen! }
    )

    useAutoScroll(scrollRef, data.length)

    /**
     * Initialize placeholder row on first render if starting fresh.
     */
    useEffect(() => {
        if (placeholderInitialized.current || from || restoredSession().restoredIndividuals) return
        if (data.length === 0) eventHandlers.setIndividualsData!(prev => [createRow<GedcomIndividual>(lastId, prev.length, GedcomIndividual)])
        placeholderInitialized.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from])

    const addRow = useCallback(() => {
        eventHandlers.setIndividualsData!(prev => {
            if (prev.length >= maxRowLimit || lastId.current >= maxRowLimit) {
                setErrorMessage(rowLimitError)
                return prev
            }
            eventHandlers.setUnsavedChanges!(true)
            return [...prev, createRow<GedcomIndividual>(lastId, prev.length, GedcomIndividual)]
        })
    }, [lastId, rowLimitError, eventHandlers])

    const onChange = useCallback((rowIndex: number, field: keyof GedcomIndividual | keyof GedcomFamily, value: any) => {
        eventHandlers.setIndividualsData!(prev => {
            const updated = [...prev]
            updated[rowIndex] = new GedcomIndividual({ ...updated[rowIndex], [field]: value, pristine: false })
            return updated
        })
        if (field === "enabled") eventHandlers.setUnsavedChanges!(true)
    }, [eventHandlers])

/* ------------------------------------------------------------------------------------------------------------------ */
/* MEMOIZED SUBCOMPONENTS                                                                                             */
/* ------------------------------------------------------------------------------------------------------------------ */

    const { fullNames, childrenMap, spousesMap } = useRelatedDeps(data, familiesData)

    const indiRows = useMemo(() => data.map((indi, rowIndex) => (
        <IndiRow
            rowIndex={rowIndex}
            selectedRowId={selectedRowId}
            setSelectedRowId={setSelectedRowId}
            row={indi}
            key={indi.id}
            lastId={lastId}
            data={data}
            familiesData={familiesData}
            familiesEnabled={familiesEnabled}
            childrenMap={childrenMap}
            spousesMap={spousesMap}
            fullNames={fullNames}
            eventHandlers={{
                switchTab: eventHandlers.switchTab,
                setIndividualsData: eventHandlers.setIndividualsData,
                setFamiliesData: eventHandlers.setFamiliesData,
                startFamily: eventHandlers.startFamily,
                setUnsavedChanges: eventHandlers.setUnsavedChanges,
                onChange: onChange,
                setError: setErrorMessage
            }}
        />
    )), [
        data,
        familiesData,
        lastId,
        familiesEnabled,
        selectedRowId,
        childrenMap,
        spousesMap,
        fullNames,
        onChange,
        eventHandlers.switchTab,
        eventHandlers.setIndividualsData,
        eventHandlers.setFamiliesData,
        eventHandlers.startFamily,
        eventHandlers.setUnsavedChanges
    ])

    const addLabel = useMemo(() => <FormattedMessage id="creator.add.individual"/>, [])

/* ------------------------------------------------------------------------------------------------------------------ */
/* TABLE ROWS                                                                                                         */
/* ------------------------------------------------------------------------------------------------------------------ */

    return (
        <div className="creator creator-indi" ref={scrollRef}>
            <Table celled definition>
                {/* Header */}
                <CreatorIndiHeader />
                {/* Data */}
                <TableBody>{indiRows}</TableBody>
                {/* Footer */}
                <CreatorTableFooter addLabel={addLabel} addRow={addRow} errorMessage={errorMessage}/>
            </Table>
        </div>
    )
}